// /pages/FleetManagement/Trucks/index.tsx
import React, { useState, useCallback, useMemo, useRef } from 'react';
import { 
    Box, 
    Heading, 
    Text, 
    HStack,
    VStack, 
    InputGroup, 
    Input, 
    InputRightElement,
    IconButton,
    Switch,
    Card,
    CardHeader,
    CardBody,
} from '@chakra-ui/react';
import { Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { apiClient } from '@/lib/api/client';
import { useToastNotification } from '@/lib/utils/useToastNotification';
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables';
import { useAccessCheck } from '@/hooks/useAccess';
import { useThemeColors } from '@/hooks/useThemeColors';
import { ActionButtons } from '@/components/common/ActionButtons';

const TrucksPage: React.FC = () => {
    const navigate = useNavigate();
    const colors = useThemeColors();
    const access = useAccessCheck();
    const tableRef = useRef<DataTableRef>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const { showError, showSuccess } = useToastNotification();

    const [searchQuery, setSearchQuery] = useState('');
    const [updatingStatus, setUpdatingStatus] = useState<Record<number, boolean>>({});

    // Pattern: Use tableParams for reactive filtering
    const tableParams = useMemo(() => ({ search: searchQuery }), [searchQuery]);

    const handleSearch = () => {
        const query = searchRef.current?.value || '';
        setSearchQuery(query); 
        tableRef.current?.applyFilter('search', query); 
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSearch();
    };

    const handleStatusToggle = useCallback(async (truckId: number, currentStatus: number) => {
        setUpdatingStatus((prev) => ({ ...prev, [truckId]: true }));
        try {
            const endpoint = currentStatus === 1 
                ? `/trucks/deactivate-truck/${truckId}` 
                : `/trucks/activate-truck/${truckId}`;
            
            await apiClient.patch(endpoint); 
            showSuccess('Success', `Truck status updated successfully.`);
            tableRef.current?.reload();
        } catch (error: any) {
            showError(error);
        } finally {
            setUpdatingStatus((prev) => {
                const updated = { ...prev };
                delete updated[truckId];
                return updated;
            });
        }
    }, [showSuccess, showError]);

    const tableOptions: DataTableOptions = useMemo(
        () => ({
            dataSource: '/trucks/truck-list', 
            primaryKey: 'id',
            showSelection: false, 
            showCheckboxes: false, // Pattern: Match ContainerYard explicit setting
            softDelete: true, 
            displayInModal: true,
            noRecordText: 'No trucks found',
            dataFields: {
                plate_number: { name: 'Plate Number', withSort: true },
                condition: { 
                    name: 'CONDITION',
                    badge: {
                        'Good': 'green', 
                        'Excellent': 'blue',
                        'Fair': 'orange',
                        'Under Maintenance': 'red', 
                        'Needs Repair': 'yellow', 
                    },
                },
                is_active: { 
                    name: 'STATUS', 
                    withSort: false,
                    maxWidth: '120px',
                },
            },
            actions: [
                {
                    label: 'Edit Truck',
                    action: 'Edit',
                    icon: 'Edit',
                    can_do: access.can_edit,
                },
            ],
            otherActions: [
              {
                name: 'Maintenance History',
                link: '/fleet-management/maintenance-history',
              },
            ]
        }),
        [access.can_edit]
    );

    const customRenderers = useMemo(
        () => ({
            is_active: (value: any, row: any) => {
                const statusValue = Number(value);
                const isUpdating = updatingStatus[row.id] || false;
                return (
                    <Switch
                        isChecked={statusValue === 1}
                        isDisabled={isUpdating || !access.can_edit}
                        colorScheme="blue" // Pattern: Match ContainerYard theme
                        onChange={() => handleStatusToggle(row.id, statusValue)}
                    />
                );
            },
        }),
        [updatingStatus, handleStatusToggle, access.can_edit]
    );

    const permissions = useMemo(() => ({
        can_edit: access.can_edit,
        can_create: access.can_create,
        can_delete: access.can_delete,
        can_view: access.can_view,
    }), [access]);

    return (
        <AdminLayout>
            <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
                <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
                    <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
                        
                        <CardHeader flexShrink={0} py={3}>
                            <Heading size="md" mb={1}>Truck Management</Heading>
                            <Text fontSize="sm" color={colors.text}>Manage trucks and their details.</Text>
                        </CardHeader>
                        
                        <CardHeader py={0} px={0} flexShrink={0}>
                            <Box pt={2} pb={0}>
                                <HStack justify="space-between" align="flex-end" spacing={4} px={5} width="100%">
                                    <Box flex="0 1 auto" minW="400px" maxW="400px">
                                        <InputGroup size="sm">
                                            <Input
                                                ref={searchRef}
                                                placeholder="Search trucks..."
                                                onKeyDown={handleKeyDown}
                                                defaultValue={searchQuery}
                                                pr="2.5rem"
                                            />
                                            <InputRightElement width="2.5rem">
                                                <IconButton
                                                    aria-label="Search"
                                                    icon={<Search size={16} />}
                                                    size="sm"
                                                    h="1.75rem"
                                                    w="1.75rem"
                                                    onClick={handleSearch}
                                                    variant="ghost"
                                                />
                                            </InputRightElement>
                                        </InputGroup>
                                    </Box>

                                    <Box>
                                        <ActionButtons
                                            pathname="/fleet-management/trucks"
                                            createLabel="Create New Truck"
                                        />
                                    </Box>
                                </HStack>
                                <Box mx={4} height={4} />
                            </Box>
                        </CardHeader>
                        
                        <CardBody
                            p={0}
                            pt={1}
                            pb={4}
                            mx={5}
                            flex="1"
                            overflow="hidden"
                            display="flex"
                            flexDirection="column"
                            minH={0}
                            position="relative"
                        >
                            <Box h="100%" display="flex" flexDirection="column" minH={0}>
                                <DataTable
                                    ref={tableRef}
                                    options={tableOptions}
                                    params={tableParams} // Explicitly pass params like ContainerYard
                                    access={permissions}
                                    customRenderers={customRenderers}
                                    onOpenModal={(idObjectOrString) => {
                                        const truckId = typeof idObjectOrString === 'object' ? idObjectOrString.id : idObjectOrString;
                                        if (truckId) navigate(`/fleet-management/trucks/${truckId}`);
                                    }}
                                />
                            </Box>
                        </CardBody>
                    </Card>
                </VStack> 
            </Box> 
        </AdminLayout>
    );
};

export default TrucksPage;